/**
 * ThinkItOverSheet - opened from a wishlist item's "Think it over" button.
 *
 * Both buying tools for ONE item, with its name and price already filled in:
 * the Should I Buy This? quiz and What Am I Giving Up? The quiz verdict is
 * saved onto the item (quiz_result) so the card can show it afterwards.
 *
 * Questions, scoring and rates are imported from QuizBox and
 * InvestmentCostCalculator so the two versions can never disagree.
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft } from "lucide-react";
import { formatCurrency } from "../../utils/formatters";
import { questions, getResult } from "../common/QuizBox";
import { RATE_PRESETS, PERIODS, CARD_COLORS } from "../common/InvestmentCostCalculator";
import { ConfirmDialog } from "../plans/plansShared";
import { usePlan } from "../../utils/plan"; // plan_10

// Fixed across themes - these are states, not decoration.
export const VERDICT_STYLE = {
  'Go For It!': { label: 'Go for it', emoji: '✅', bg: '#E4F1E7', color: '#2F6B44' },
  'Sleep On It': { label: 'Sleep on it', emoji: '😴', bg: '#FDF0E2', color: '#8A5A1E' },
  'Skip It!': { label: 'Skip it', emoji: '🚫', bg: '#FDE7EC', color: '#BE123C' },
};

const serif = { fontFamily: "'DM Serif Display', serif", fontWeight: 400 };

const ThinkItOverSheet = ({ item, taxRate = 0, theme = 'pink', onClose, onSaveResult, onRemove }) => {
  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';
  const plan = usePlan(); // plan_10
  const accent = plan ? '#7A5DD3' : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE';
  const eyebrow = plan ? '#7A5DD3' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const chip = plan ? '#ECE6FA' : isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';
  const mainBtn = plan ? 'bg-[#1F1E24]' : isPink ? 'bg-pink-500' : isWarm ? 'bg-[#2D5A3D]' : 'bg-indigo-500';
  // Giving up tab: the calculator's own colours, per theme.
  const presetGradient = plan ? '#1F1E24' : isWarm ? 'linear-gradient(135deg, #2D5A3D, #1B4027)'
    : isPink ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)' : 'linear-gradient(135deg, #4361EE, #6366f1)';
  const costColor = plan ? '#7A5DD3' : isWarm ? '#8B7355' : isPink ? '#E27BA1' : '#4338ca';
  const bestBadge = plan ? 'bg-[#1F1E24] text-white' : isWarm ? 'bg-[#8B7355] text-white' : isPink ? 'bg-pink-500 text-white' : 'bg-indigo-600 text-white';

  const [mode, setMode] = useState('quiz');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [preset, setPreset] = useState(0);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Freeze the page underneath while the sheet is open. overflow:hidden alone
  // does not stop iOS scrolling the body, so the body is pinned in place and
  // restored to the same scroll position afterwards.
  useEffect(() => {
    const y = window.scrollY;
    const b = document.body.style;
    const prev = { position: b.position, top: b.top, left: b.left, right: b.right, width: b.width, overflow: b.overflow };
    b.position = 'fixed'; b.top = '-' + y + 'px'; b.left = '0'; b.right = '0'; b.width = '100%'; b.overflow = 'hidden';
    return () => {
      Object.assign(b, prev);
      window.scrollTo(0, y);
    };
  }, []);

  const price = Number(item.value) || 0;
  const withTax = price * (1 + (Number(taxRate) || 0) / 100);
  const daysOnList = item.date_added
    ? Math.max(0, Math.floor((Date.now() - new Date(item.date_added).getTime()) / 86400000))
    : null;

  const q = questions[step];
  const result = done ? getResult(answers) : null;

  const pick = (opt) => {
    const next = { ...answers, [q.id]: opt };
    setAnswers(next);
    if (step < questions.length - 1) {
      setStep(step + 1);
      return;
    }
    setDone(true);
    const r = getResult(next);
    // Saved as soon as the quiz is finished, so closing the sheet still counts.
    onSaveResult?.({ verdict: r.verdict, score: r.score, at: new Date().toISOString() });
  };

  const restart = () => { setStep(0); setAnswers({}); setDone(false); };

  const presets = RATE_PRESETS.filter((p) => p.rate !== null);
  const rate = presets[preset] ? presets[preset].rate : 7;
  const grow = (years) => withTax * Math.pow(1 + rate / 100, years);
  const longest = PERIODS[PERIODS.length - 1];

  const thumb = item.image_url ? (
    <img src={item.image_url} alt="" className="w-full h-full object-cover" />
  ) : (
    <span style={{ fontSize: '26px' }}>{item.emoji || '🛍️'}</span>
  );

  const actions = (
    <div className="flex gap-2 mt-4">
      <button
        onClick={onClose}
        className="flex-1 py-3 rounded-2xl text-[14px] font-bold bg-white border border-gray-200 text-gray-700"
      >
        Keep on list
      </button>
      <button
        onClick={() => setConfirmRemove(true)}
        className={`flex-1 py-3 rounded-2xl text-[14px] font-bold text-white ${mainBtn}`}
      >
        Remove it
      </button>
    </div>
  );

  return createPortal(
    <>
    <div className="fixed inset-0 z-[9998] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" style={{ touchAction: 'none' }} />
      <div
        className="relative w-full bg-[#FBF7F9] rounded-t-3xl shadow-2xl flex flex-col wy-plan-think"
        style={{ maxHeight: '92vh', fontFamily: "'DM Sans', sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div
          className="flex-1 overflow-y-auto px-5"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }}
        >
          {/* The item being thought about */}
          <div className="flex items-center gap-3 bg-white rounded-2xl p-2.5 shadow-sm">
            <div
              className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
              style={{ background: chip }}
            >
              {thumb}
            </div>
            <div className="flex-1 min-w-0">
              <p className="m-0 text-[14px] font-semibold text-gray-800 truncate">{item.name}</p>
              <p className="m-0 text-[14px] font-bold" style={{ color: accent }}>
                {formatCurrency(price)}
                <span className="text-[11.5px] font-medium text-gray-400">{' · '}{formatCurrency(withTax)} with tax</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Two tools, one item */}
          <div className="flex rounded-2xl p-1 my-3.5" style={{ background: plan ? '#ECE8F4' : '#EFE4EB', borderRadius: 999 }}>
            {[['quiz', '🤔 Should I buy?'], ['cost', '📈 Giving up']].map(([k, lab]) => (
              <button
                key={k}
                onClick={() => setMode(k)}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold"
                style={mode === k
                  ? { background: '#fff', color: '#1F2937', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }
                  : { color: '#8A8290' }}
              >
                {lab}
              </button>
            ))}
          </div>

          {mode === 'quiz' && !done && (
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="m-0 text-[11px] font-bold tracking-[0.1em]" style={{ color: eyebrow }}>
                  QUESTION {step + 1} OF {questions.length}
                </p>
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="flex items-center text-[12px] font-semibold text-gray-400">
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                  </button>
                )}
              </div>
              <div className="h-[5px] rounded-full mt-2 mb-3.5 overflow-hidden" style={{ background: '#F1E8EE' }}>
                <div className="h-full rounded-full" style={{ width: ((step + 1) / questions.length) * 100 + '%', background: accent }} />
              </div>
              <h3 className="m-0 text-[21px] leading-tight text-gray-800" style={serif}>{q.question}</h3>
              {q.id === 4 && daysOnList !== null && (
                <p className="m-0 mt-1.5 text-[12.5px] text-gray-400">
                  It's been on your wishlist for {daysOnList} day{daysOnList === 1 ? '' : 's'}.
                </p>
              )}
              <div className="mt-3.5 space-y-2">
                {q.options.map((opt) => {
                  const selected = answers[q.id] && answers[q.id].label === opt.label;
                  return (
                    <button
                      key={opt.label}
                      onClick={() => pick(opt)}
                      className="w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-left"
                      style={{ borderColor: selected ? accent : '#F1E8EE', background: selected ? chip : '#fff' }}
                    >
                      <span className="text-[22px] flex-shrink-0">{opt.emoji}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13.5px] font-semibold text-gray-800 leading-tight">{opt.label}</span>
                        <span className="block text-[11px] text-gray-400 mt-0.5">{opt.subtitle}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {mode === 'quiz' && done && result && (() => {
            const v = VERDICT_STYLE[result.verdict] || VERDICT_STYLE['Sleep On It'];
            return (
              <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 text-[34px]" style={{ background: v.bg }}>
                  {v.emoji}
                </div>
                <h3 className="m-0 text-[26px] text-gray-900" style={serif}>{result.verdict}</h3>
                <p className="m-0 mt-1.5 text-[13px] text-gray-500 leading-relaxed">{result.message}</p>
                <div className="mt-4 rounded-2xl p-3" style={{ background: '#FBF7F9' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[12.5px] font-semibold text-gray-600">Purchase score</span>
                    <span className="text-[18px] font-bold" style={{ ...serif, color: v.color }}>{result.score} <span className="text-gray-300">/ 100</span></span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: result.score + '%', background: v.color }} />
                  </div>
                </div>
                <button
                  onClick={() => setMode('cost')}
                  className="mt-3 w-full py-2.5 rounded-2xl text-[13px] font-semibold"
                  style={{ background: chip, color: eyebrow }}
                >
                  {'📈'} See what you'd be giving up
                </button>
                <button onClick={restart} className="mt-2 text-[12.5px] font-semibold text-gray-400">
                  Retake the quiz
                </button>
                {actions}
              </div>
            );
          })()}

          {mode === 'cost' && (
            <div className="px-1">
              <p className="m-0 text-[13px] text-gray-500">Invested instead, {formatCurrency(withTax)} could become</p>
              <p className="m-0 mt-1 text-[38px] leading-none" style={{ ...serif, color: costColor }}>{formatCurrency(grow(longest))}</p>
              <p className="m-0 mt-1 text-[13px] text-gray-500">in {longest} years</p>

              <div className="flex gap-2 mt-4">
                {presets.map((p, i) => (
                  <button
                    key={p.label}
                    onClick={() => setPreset(i)}
                    className={`flex-1 py-2.5 rounded-2xl text-center transition-all ${preset === i ? 'text-white shadow-md' : 'bg-white text-gray-700 border border-gray-100 shadow-sm'}`}
                    style={preset === i ? { background: presetGradient } : undefined}
                  >
                    <span className="text-sm font-bold block">{p.label}</span>
                    <span className={`text-xs ${preset === i ? 'text-white/80' : 'text-gray-400'}`}>{p.rate}%</span>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                {PERIODS.map((y, idx) => {
                  const c = CARD_COLORS[idx] || CARD_COLORS[0];
                  const fv = grow(y);
                  const gain = fv - withTax;
                  const best = y === longest;
                  return (
                    <div key={y} className={`rounded-2xl p-4 border relative ${c.bg} ${c.border}`}>
                      {best && (
                        <span className={`absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${bestBadge}`}>Best</span>
                      )}
                      <p className={`m-0 text-xs font-bold uppercase tracking-wider mb-2 ${c.yearText}`}>{y} Years</p>
                      <p className={`m-0 text-2xl font-bold mb-1.5 ${best ? c.yearText : 'text-gray-800'}`} style={serif}>
                        {formatCurrency(fv)}
                      </p>
                      <p className="m-0 text-xs font-bold text-emerald-500">&#8593; +{formatCurrency(gain)}</p>
                      <p className="m-0 text-xs text-gray-400 mt-0.5">{withTax > 0 ? ((gain / withTax) * 100).toFixed(0) : 0}% growth</p>
                    </div>
                  );
                })}
              </div>
              <p className="m-0 mt-3 text-[11px] text-gray-400 leading-snug">
                Based on historical average annual returns, including {Number(taxRate) || 0}% tax on the price. Past performance does not guarantee future results.
              </p>
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>

      {confirmRemove && (
        <ConfirmDialog
          title="Remove from wishlist?"
          body={item.name + ' will be removed from your list.'}
          confirmLabel="Remove"
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() => { setConfirmRemove(false); onRemove?.(); }}
        />
      )}
    </>,
    document.body
  );
};

export default ThinkItOverSheet;
